import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Marca } from '../../componentes/Marca';
import { GUIAS } from '../../conteudo/guias';
import {
  ANO_INICIO,
  CNPJ,
  MARCA_REGISTRADA,
  NOME_FANTASIA,
} from './constantes';
import css from './Rodape.module.css';

/**
 * Rodapé.
 *
 * Além do visual, cumpre três funções concretas:
 *
 * 1. **Identifica quem é a empresa.** Rodapé sem CNPJ nem contato é o sinal
 *    que mais derruba confiança em serviço que vai receber pagamento — e o
 *    Google trata a mesma ausência como falta de credibilidade da página.
 * 2. **Distribui autoridade interna.** Os links repetem os termos que
 *    importam para busca em texto âncora, apontando para as seções da página.
 * 3. **Declara a marca.** O `®` só vale depois do registro concedido no INPI;
 *    até lá, o correto é `™`. Ver ANO_INICIO/MARCA_REGISTRADA em `constantes`.
 */
export function Rodape() {
  return (
    <footer className={css.rodape}>
      <div className={css.conteudo}>
        <div className={css.grade}>
          <div className={css.marca}>
            <Marca tamanho={22} fonte={18} />
            <p className={css.resumo}>
              XML de NF-e para escritórios contábeis e fiscais — pela chave de
              acesso, direto do SPED ou capturados da SEFAZ com o certificado A1
              do cliente.
            </p>
            <div className={css.empresa}>
              <span>{NOME_FANTASIA}</span>
              <span>CNPJ {CNPJ}</span>
            </div>
          </div>

          <Coluna titulo="O serviço">
            <LinkRodape href="/#como-funciona">Como funciona</LinkRodape>
            <LinkRodape href="/#escritorios">Para escritórios</LinkRodape>
            <LinkRodape href="/#captura">Captura automática</LinkRodape>
            <LinkRodape href="/#precos">Preços</LinkRodape>
            <LinkRodape href="/#seguranca">Segurança</LinkRodape>
            <LinkRodape href="/#duvidas">Dúvidas frequentes</LinkRodape>
            <LinkRodape para="/desenvolvedores" clique="rodape-api">
              API para desenvolvedores
            </LinkRodape>
          </Coluna>

          <Coluna titulo="Guias">
            {GUIAS.map((g) => (
              <LinkRodape key={g.rota} para={g.rota} clique="rodape-guia">
                {g.rotulo}
              </LinkRodape>
            ))}
          </Coluna>

          <Coluna titulo="Conta">
            <LinkRodape para="/criar-conta">Criar conta grátis</LinkRodape>
            <LinkRodape para="/entrar">Entrar</LinkRodape>
          </Coluna>

          <Coluna titulo="Legal">
            <LinkRodape para="/privacidade">Política de Privacidade</LinkRodape>
            <LinkRodape para="/privacidade#cookies">
              Preferências de cookies
            </LinkRodape>
          </Coluna>

          <Coluna titulo="Contato">
            <LinkRodape para="/painel/suporte">Falar com o suporte</LinkRodape>
            <span className={css.nota}>
              Atendimento pelo chat do painel, em dias úteis.
            </span>
          </Coluna>
        </div>

        <div className={css.base}>
          <span>
            © {intervaloDeAnos()} {NOME_FANTASIA}
            {MARCA_REGISTRADA ? '®' : '™'}. Todos os direitos reservados.
          </span>
          <span>
            {NOME_FANTASIA} — CNPJ {CNPJ} · Atendimento pelo chat de suporte
          </span>
        </div>

        <p className={css.legal}>
          Serviço independente de consulta e download de documentos fiscais
          eletrônicos. Não temos vínculo com a Secretaria da Fazenda, a Receita
          Federal ou qualquer órgão público. Os XMLs pertencem ao contribuinte;
          apenas obtemos e entregamos os arquivos a quem informa a chave de
          acesso ou autoriza a consulta com o próprio certificado. NF-e, NFC-e,
          CT-e e SPED são termos definidos pela legislação fiscal brasileira. Na
          captura automática, o certificado digital do cliente é usado só para
          consultar a SEFAZ e registrar a Ciência da Operação, nos termos da
          Política de Privacidade.
        </p>
      </div>
    </footer>
  );
}

/** "2026" no primeiro ano, "2026–2028" depois. Evita rodapé com ano parado. */
function intervaloDeAnos(): string {
  const atual = new Date().getFullYear();
  return atual > ANO_INICIO ? `${ANO_INICIO}–${atual}` : String(ANO_INICIO);
}

function Coluna({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className={css.coluna}>
      <span className={css.tituloColuna}>{titulo}</span>
      {children}
    </div>
  );
}

/** Link do rodapé: interno (`para`) ou âncora/externo (`href`). */
function LinkRodape({
  para,
  href,
  clique,
  children,
}: {
  para?: string;
  href?: string;
  /** Nome do alvo para a medição de cliques (`util/clique.ts`). */
  clique?: string;
  children: ReactNode;
}) {
  return para ? (
    <Link to={para} className={css.link} data-clique={clique}>
      {children}
    </Link>
  ) : (
    <a href={href} className={css.link}>
      {children}
    </a>
  );
}
